/**
 * THUNK HELPERS — Utility condivise dai thunk del core e dei plugin.
 *
 * Vivere qui (sotto store/) perche' sono strettamente legati a `RootState`
 * e alla shape dell'auth state. I thunk delle singole entita' li importano
 * evitando di ripetere boilerplate.
 */

import type { RootState } from './store';

/**
 * Legge il `groupId` (sito attivo) dallo state auth.
 * Lancia se l'utente non e' autenticato — da chiamare dentro un try/catch
 * di un thunk, che convertira' l'eccezione in rejectWithValue tramite
 * `extractError`.
 *
 * Uso tipico:
 * ```ts
 * export const fetchFoos = createAsyncThunk<Foo[], void, { state: RootState }>(
 *   'foos/fetchAll',
 *   async (_, { getState, rejectWithValue }) => {
 *     try {
 *       return await fooApi.getAll(requireGroupId(getState));
 *     } catch (err: unknown) {
 *       return rejectWithValue(extractError(err));
 *     }
 *   }
 * );
 * ```
 */
export function requireGroupId(getState: () => RootState): number {
  const groupId = getState().auth.user?.defaultGroupId;
  if (!groupId) throw new Error('Nessun groupId (utente non autenticato)');
  return groupId;
}

/**
 * Estrae il messaggio di errore leggibile da un errore qualsiasi.
 * Preferisce in ordine:
 *   1. Il `title` o `message` del body JSON della response HTTP (es.
 *      errori Liferay REST con shape `{ status, title, type }`).
 *   2. `err.message` per Error generici.
 *   3. 'Errore' fallback.
 *
 * Uso tipico dentro i thunk (vedi createCrudThunks): il valore passa a
 * `rejectWithValue` e l'UI lo mostra con `useToast` / `.unwrap().catch()`.
 */
export function extractError(err: unknown): string {
  if (err instanceof Error) {
    const response = (err as { response?: { data: unknown } }).response;
    if (response?.data && typeof response.data === 'object') {
      const body = response.data as { title?: string; message?: string };
      if (body.title) return body.title;
      if (body.message) return body.message;
    }
    return err.message;
  }
  return 'Errore';
}
